// Client-only stable id for a milestone. The editor tracks "which milestone is
// armed / being edited" by this id instead of array index, so deleting or
// reordering milestones can't silently retarget the wrong one. The backend
// matches milestones by position, not id, so `_uid` is stripped before save
// (see extractMilestoneIconUploads).
export const newMilestoneId = () =>
  crypto.randomUUID?.() ??
  `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Ensures every milestone carries a stable `_uid`, minting one only where it's
// missing (preserves ids on milestones that already have them). Use when
// seeding milestones or loading them from the backend.
export const withMilestoneIds = (milestones) =>
  (Array.isArray(milestones) ? milestones : []).map((m) =>
    m?._uid ? m : { ...m, _uid: newMilestoneId() },
  );

// Canonical milestone order: ascending by stamp position, so the editor renders
// them in stamp-progression order (highest position = "Final Reward" = last
// card, numbering ascends). Returns a new array; non-arrays become [].
export const sortMilestonesByPosition = (milestones) =>
  (Array.isArray(milestones) ? [...milestones] : []).sort(
    (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0),
  );

const stripMilestoneIds = (milestones) =>
  Array.isArray(milestones)
    ? // eslint-disable-next-line no-unused-vars
      milestones.map(({ _uid, ...rest }) => rest)
    : milestones;

// The milestone at the highest stamp position is the "Final Reward" — redeeming
// it completes the cycle and resets the card. A lone milestone is itself the
// final reward. Computed from the position, not list order, since a merchant can
// reorder positions freely. -1 for an empty list.
export const finalMilestoneIndex = (milestones = []) =>
  milestones.length > 0
    ? milestones.reduce(
        (maxI, m, i, arr) =>
          Number(m.position) > Number(arr[maxI].position) ? i : maxI,
        0,
      )
    : -1;

// Whether a card's rewards are the milestone editor's to validate. The single
// source for this: the form and the save path used to test slightly different
// conditions (the form checked only the type), which meant a non-milestone stamp
// mode would have painted fields red while save quietly succeeded.
export const usesMilestoneRewards = (formData) =>
  formData?.type === "stamp" && formData?.stampRewardMode === "milestone";

// Milestone editor validation. Each milestone becomes a redemption campaign
// (point = position, description = label) on the backend, so two milestones at
// the same position collide into ambiguous campaigns and an empty label renders
// as a blank reward. Returns per-index errors plus an isValid flag; callers show
// the errors inline and block save. Only meaningful in milestone mode.
export const validateMilestones = (milestones = []) => {
  const errors = {};

  const positionCounts = milestones.reduce((counts, m) => {
    const pos = Number(m.position) || 0;
    counts[pos] = (counts[pos] || 0) + 1;
    return counts;
  }, {});

  milestones.forEach((m, index) => {
    const rowErrors = {};

    if (!String(m.label || "").trim()) {
      rowErrors.label = "required";
    }

    const pos = Number(m.position) || 0;
    if (pos < 1) {
      rowErrors.position = "invalid";
    } else if (positionCounts[pos] > 1) {
      rowErrors.position = "duplicate";
    }

    if (Object.keys(rowErrors).length > 0) {
      errors[index] = rowErrors;
    }
  });

  return { errors, isValid: Object.keys(errors).length === 0 };
};

// Splits freshly chosen milestone icons out of the pass data for upload. A
// custom icon picked this session is a data:/blob: URL; each becomes a
// milestoneIcon_<index> file and that milestone's icon is blanked so the backend
// fills it from the upload. Library picks and previously-saved icons are http
// URLs and pass through untouched. Returns the (possibly rewritten) layout plus
// the upload FormData.
export const extractMilestoneIconUploads = async (formData) => {
  const milestoneIconsFormData = new FormData();

  if (
    formData.stampRewardMode !== "milestone" ||
    !Array.isArray(formData.milestones)
  ) {
    return {
      layoutData: {
        ...formData,
        milestones: stripMilestoneIds(formData.milestones),
      },
      milestoneIconsFormData,
    };
  }

  const milestones = await Promise.all(
    formData.milestones.map(async (milestone, index) => {
      // eslint-disable-next-line no-unused-vars
      const { _uid, ...rest } = milestone;
      const icon = rest.icon;
      if (icon && (icon.startsWith("data:") || icon.startsWith("blob:"))) {
        const blob = await (await fetch(icon)).blob();
        milestoneIconsFormData.append(
          `milestoneIcon_${index}`,
          blob,
          `milestone-${index}.png`,
        );
        return { ...rest, icon: null };
      }
      return rest;
    }),
  );

  return {
    layoutData: { ...formData, milestones },
    milestoneIconsFormData,
  };
};

// The wallet labels its own fields — the customer's device language decides the
// wording, resolved from the backend's config/locales/wallet/*.yml
// (wallet.labels.*) via the pass's .lproj strings on Apple and
// find_localized_label on Google. The pass JSON we save carries only the field
// TYPE (member / rewards / stamps / …), never the label text, so these strings
// exist purely to make the dashboard preview read like the real card. They are
// verbatim copies of wallet.labels.* under passes.form.walletLabels.* — keep the
// two in sync when either side changes wording.
export const WALLET_LABEL_FIELDS = [
  "member",
  "points",
  "stamps",
  "balance",
  "balance_money",
  "birthday",
  "discount",
  "rewards",
  "tier",
  "valid_until",
  "visits",
];

// Resolve the label set for the active dashboard language, keyed by field type.
export const walletLabels = (t) =>
  Object.fromEntries(
    WALLET_LABEL_FIELDS.map((field) => [
      field,
      t(`passes.form.walletLabels.${field}`),
    ]),
  );

// Value-based dependency for the updatePreview effects. Locale JSON loads
// asynchronously, so the first render resolves these to raw key paths; keying
// the effect on the resolved TEXT re-runs it exactly once the real strings
// arrive (and on every language switch) without depending on `t`, whose identity
// changes every provider render and would loop.
export const walletLabelsSignature = (labels) => Object.values(labels).join("|");

// Mirrors the backend StampMilestoneUtils.reward_field_value so the pass preview
// reads what the wallet's REWARD field will show as a customer stamps their
// card. `position` is the current stamp count (the preview drives it with
// initialPoints). Nothing is "claimed" in the preview, so a reached milestone is
// treated as claimable: the earliest reached reward is gift-marked (🎁 label),
// otherwise the nearest upcoming one shows a localized "N to go". Pass `t` to
// localize that phrase (the card localizes it in the pass's language); without
// it the count falls back to a bare number. Empty ladder → "".
//
// A banked final reward also carries a quantity ("🎁 Coffee ×2"), for the same
// reason the card does: the name alone cannot tell one unredeemed coffee from
// three.
export const rewardFieldPreview = (position = 0, milestones = [], t) => {
  const pos = Number(position) || 0;
  const sorted = milestones
    .map((m) => ({
      position: Number(m.position) || 0,
      label: String(m.label || "").trim(),
    }))
    // Skip unnamed milestones (a fresh card seeds one with a blank label) so the
    // preview never renders " in 6" / "🎁  ready" with a missing reward name.
    .filter((m) => m.label)
    .sort((a, b) => a.position - b.position);

  const claimable = sorted.find((m) => pos >= m.position);
  if (claimable) {
    // The final reward is the only one that banks up: redeeming it subtracts a
    // whole cycle, so the number still redeemable is how many cycles the
    // balance holds. An intermediate milestone is claimable once per cycle, so
    // it never carries a quantity. Mirrors
    // StampMilestoneUtils.final_reward_suffix.
    const finalMilestone = sorted[sorted.length - 1];
    const available =
      claimable.position === finalMilestone.position && finalMilestone.position > 0
        ? Math.floor(pos / finalMilestone.position)
        : 1;

    return available >= 2
      ? `🎁 ${claimable.label} ×${available}`
      : `🎁 ${claimable.label}`;
  }

  const upcoming = sorted.find((m) => pos < m.position);
  if (upcoming) {
    const n = upcoming.position - pos;
    const remaining = t
      ? t("passes.form.milestones.remaining", { count: n })
      : `${n}`;
    return `${upcoming.label} · ${remaining}`;
  }

  return "";
};

// Mirrors the backend StampMilestoneUtils.reward_summary — the multi-line reward
// list the wallet always renders on the BACK of a milestone card. The preview
// has no customer, so nothing is "claimed": a reached reward is gift-marked
// (🎁 label (pos)), an upcoming one shows "label (pos) · N to go". `t` localizes
// the "N to go" like the card does. Returns "" when there are no named rewards
// (so the caller can fall back to the normal empty-back placeholder).
export const rewardSummaryPreview = (position = 0, milestones = [], t) => {
  const pos = Number(position) || 0;
  const sorted = milestones
    .map((m) => ({
      position: Number(m.position) || 0,
      label: String(m.label || "").trim(),
    }))
    .filter((m) => m.label && m.position > 0)
    .sort((a, b) => a.position - b.position);

  if (sorted.length === 0) return "";

  return sorted
    .map((m) => {
      if (pos >= m.position) return `🎁 ${m.label} (${m.position})`;
      const n = m.position - pos;
      const remaining = t
        ? t("passes.form.milestones.remaining", { count: n })
        : `${n}`;
      return `${m.label} (${m.position}) · ${remaining}`;
    })
    .join("\n");
};

export const getAmountTextFromType = (type, currency, amount_type) => {
  if (amount_type === "multiplier") return "X";
  if (type === "coupon") return "USE";
  if (type === "cashback") return "POINTS";
  if (type === "stamp") return "STAMPS";
  if (type === "balance") return "CREDIT";
  if (type === "membership") return "VISITS";
  if (type === "cashback_money") return currency;

  return "UNDEFINED";
};
